import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Copy, Trash2, Star, Download, Search } from "lucide-react";
import { http, formatSoles, fileUrl } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { PageHeader, Confirm, SortableList, Pill, exportXlsx } from "./ui";
import ProductEditor from "./ProductEditor";

function QuickPrice({ p, onSaved }) {
  const [v, setV] = useState(p.retail_price);
  useEffect(() => setV(p.retail_price), [p.retail_price]);
  async function save() {
    if (Number(v) === Number(p.retail_price)) return;
    try {
      const { data } = await http.patch(`/products/${p.id}`, { retail_price: Number(v) });
      toast.success("Precio guardado");
      onSaved(data);
    } catch {
      toast.error("No se pudo guardar el precio");
      setV(p.retail_price);
    }
  }
  return <Input type="number" step="0.01" value={v} onChange={(e) => setV(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.target.blur()} className="h-8 w-24 text-sm" data-testid={`quick-price-${p.code}`} />;
}

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");
  const [status, setStatus] = useState("");
  const [sel, setSel] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => http.get("/products", { params: { active_only: false } }).then((r) => setProducts(r.data));
  useEffect(() => { load(); http.get("/categories").then((r) => setCategories(r.data)); }, []);

  const replace = (np) => setProducts((ps) => ps.map((p) => (p.id === np.id ? np : p)));
  const catName = (s) => categories.find((c) => c.slug === s)?.name || s;

  const list = useMemo(() => products.filter((p) => {
    const t = q.toLowerCase();
    if (t && !`${p.code} ${p.name}`.toLowerCase().includes(t)) return false;
    if (cat && p.category !== cat) return false;
    if (status === "active" && p.is_active === false) return false;
    if (status === "inactive" && p.is_active !== false) return false;
    if (status === "featured" && !p.featured) return false;
    return true;
  }), [products, q, cat, status]);

  async function reorder(newList) {
    const ids = new Set(newList.map((p) => p.id));
    let k = 0;
    const merged = products.map((p) => (ids.has(p.id) ? newList[k++] : p));
    setProducts(merged);
    try {
      await http.post("/products/reorder", { ids: merged.map((p) => p.id) });
      toast.success("Orden actualizado");
    } catch {
      toast.error("No se pudo guardar el orden");
      load();
    }
  }

  async function bulk(action, value) {
    if (!sel.length) return;
    await http.post("/products/bulk", { ids: sel, action, value });
    toast.success("Acción aplicada");
    setSel([]);
    load();
  }

  async function create() {
    const { data } = await http.post("/products", {
      code: `FR-NEW-${Date.now().toString().slice(-4)}`, name: "Nuevo producto", category: cat || categories[0]?.slug || "packs",
      retail_price: 0, is_active: false, sort_order: -1,
    });
    setProducts((ps) => [data, ...ps]);
    setEditing(data);
  }

  async function duplicate(p) {
    const { data } = await http.post(`/products/${p.id}/duplicate`);
    toast.success("Producto duplicado (inactivo)");
    await load();
    setEditing(data);
  }

  async function remove(p) {
    await http.delete(`/products/${p.id}`);
    toast.success("Producto eliminado");
    load();
  }

  const toggle = async (p, k, v) => replace((await http.patch(`/products/${p.id}`, { [k]: v })).data);
  const allSel = list.length > 0 && list.every((p) => sel.includes(p.id));

  function exportRows() {
    exportXlsx(list.map((p) => ({
      Código: p.code, Nombre: p.name, Categoría: catName(p.category), Subcategoría: p.subcategory || "",
      "Precio minorista": p.retail_price, "Precio promo": p.promo_price || "",
      Mayorista: (p.wholesale_tiers || []).map((t) => `${t.min_qty}+: ${t.price}`).join(" | "),
      Stock: p.stock_status === "in_stock" ? "En stock" : "Bajo pedido", Activo: p.is_active !== false ? "Sí" : "No", Destacado: p.featured ? "Sí" : "No",
    })), "productos-floral-rosse", "Productos");
  }

  const row = (p) => (
    <div className={`bg-white rounded-xl border border-black/5 p-3 flex flex-wrap sm:flex-nowrap items-center gap-3 ${p.is_active === false ? "opacity-60" : ""}`} data-testid={`admin-product-${p.code}`}>
      <Checkbox checked={sel.includes(p.id)} onCheckedChange={(v) => setSel((s) => (v ? [...s, p.id] : s.filter((x) => x !== p.id)))} data-testid={`select-product-${p.code}`} />
      {p.images?.[0] ? <img src={fileUrl(p.images[0])} alt="" className="h-12 w-12 rounded-lg object-cover" /> : <div className="h-12 w-12 rounded-lg bg-neutral-100" />}
      <div className="flex-1 min-w-[140px]">
        <div className="text-[10px] uppercase tracking-widest text-neutral-400 font-semibold">{p.code} · {catName(p.category)}</div>
        <div className="font-semibold leading-tight">{p.name}</div>
        <div className="flex gap-1 mt-1 flex-wrap">
          <Pill className={p.stock_status === "in_stock" ? "badge-stock" : "badge-preorder"}>{p.stock_status === "in_stock" ? "Stock" : "Bajo pedido"}</Pill>
          {p.promo_price && <Pill className="bg-red-50 text-red-700">Promo {formatSoles(p.promo_price)}</Pill>}
          {p.wholesale_tiers?.length > 0 && <Pill className="bg-neutral-100 text-neutral-600">{p.wholesale_tiers.length} niveles mayoristas</Pill>}
        </div>
      </div>
      <div className="flex items-center gap-1"><span className="text-xs text-neutral-500">S/</span><QuickPrice p={p} onSaved={replace} /></div>
      <button onClick={() => toggle(p, "featured", !p.featured)} title="Favorito de temporada" data-testid={`feature-${p.code}`}>
        <Star size={18} className={p.featured ? "fill-amber-400 text-amber-400" : "text-neutral-300"} />
      </button>
      <Switch checked={p.is_active !== false} onCheckedChange={(v) => toggle(p, "is_active", v)} data-testid={`active-${p.code}`} />
      <div className="flex gap-1">
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => setEditing(p)} data-testid={`edit-product-${p.code}`}><Pencil size={14} /></Button>
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => duplicate(p)} data-testid={`duplicate-product-${p.code}`}><Copy size={14} /></Button>
        <Confirm onConfirm={() => remove(p)} title={`¿Eliminar ${p.name}?`} trigger={<Button size="icon" variant="outline" className="h-8 w-8" data-testid={`delete-product-${p.code}`}><Trash2 size={14} /></Button>} />
      </div>
    </div>
  );

  return (
    <div data-testid="admin-products-page">
      <PageHeader title="Productos" subtitle={`${products.length} productos · arrastra para ordenar cómo aparecen en la tienda`}
        actions={<>
          <Button variant="outline" onClick={exportRows} data-testid="export-products"><Download size={14} className="mr-1" /> Excel</Button>
          <Button className="btn-primary" onClick={create} data-testid="new-product-btn"><Plus size={14} className="mr-1" /> Nuevo producto</Button>
        </>} />
      <div className="flex flex-wrap gap-2 mb-4">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por código o nombre" className="pl-9 bg-white" data-testid="product-search" />
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} className="h-10 rounded-md border border-input px-3 text-sm bg-white" data-testid="product-filter-category">
          <option value="">Todas las categorías</option>
          {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-10 rounded-md border border-input px-3 text-sm bg-white" data-testid="product-filter-status">
          <option value="">Todos</option><option value="active">Activos</option><option value="inactive">Inactivos</option><option value="featured">Favoritos de temporada</option>
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-4 bg-white rounded-xl border border-black/5 p-2" data-testid="product-bulk-bar">
        <Checkbox checked={allSel} onCheckedChange={(v) => setSel(v ? list.map((p) => p.id) : [])} data-testid="select-all-products" />
        <span className="text-sm font-semibold mr-2">{sel.length} seleccionados</span>
        {sel.length > 0 && <>
          <Button size="sm" variant="outline" onClick={() => bulk("activate")} data-testid="bulk-activate">Activar</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("deactivate")} data-testid="bulk-deactivate">Desactivar</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("feature")}>Destacar</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("unfeature")}>Quitar destacado</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("in_stock")}>En stock</Button>
          <Button size="sm" variant="outline" onClick={() => bulk("pre_order")}>Bajo pedido</Button>
          <select defaultValue="" onChange={(e) => e.target.value && bulk("category", e.target.value)} className="h-8 rounded-md border border-input px-2 text-xs bg-white">
            <option value="">Mover a categoría…</option>
            {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <Button size="sm" variant="outline" onClick={() => { const v = window.prompt("Ajustar precios en % (ej. 10 o -5):"); if (v && !isNaN(Number(v))) bulk("price_percent", Number(v)); }} data-testid="bulk-price">Ajustar precio %</Button>
          <Confirm onConfirm={() => bulk("delete")} title={`¿Eliminar ${sel.length} productos?`} trigger={<Button size="sm" variant="outline" className="text-red-600" data-testid="bulk-delete">Eliminar</Button>} />
        </>}
      </div>
      {q ? <div className="space-y-2">{list.map((p) => <div key={p.id}>{row(p)}</div>)}</div>
        : <SortableList items={list} onReorder={reorder} renderItem={row} />}
      {list.length === 0 && <p className="text-sm text-neutral-500 py-10 text-center">No hay productos con esos filtros.</p>}
      <ProductEditor product={editing} categories={categories} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSaved={replace} />
    </div>
  );
}
